import React, { useState, useEffect, useRef } from 'react';
import './WhatsAppWidget.css';

/**
 * WhatsApp Widget Component
 * Sağ alt köşede sürüklenebilir WhatsApp destek butonu
 */
const WhatsAppWidget = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false); // Minimize/maximize state
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ bottom: 80, right: 24 });
  const dragStartPos = useRef({ x: 0, y: 0 });
  const widgetRef = useRef(null);
  const longPressTimer = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Mobil mi kontrol et
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // LocalStorage'dan pozisyonu yükle
  useEffect(() => {
    const savedPosition = localStorage.getItem('whatsapp-widget-position');
    
    // Desktop için saved position kullan, mobil için sabit pozisyon
    if (savedPosition && !isMobile) {
      setPosition(JSON.parse(savedPosition));
    } else if (isMobile) {
      // Mobilde her zaman sağ alt köşede, bottom nav'ın üstünde
      setPosition({ bottom: 80, right: 16 });
    }
  }, [isMobile]);
  
  // Pozisyonu kaydet
  const savePosition = (newPosition) => {
    localStorage.setItem('whatsapp-widget-position', JSON.stringify(newPosition));
  };
  
  // WhatsApp Business numaranızı buraya girin (ülke kodu ile birlikte, + işareti olmadan)
  // Örnek: 905551234567 (Türkiye için 90, sonra numara)
  const phoneNumber = '905552609420'; // Minela Kids WhatsApp Business
  
  // Ön tanımlı mesaj
  const defaultMessage = 'Merhaba! Minela Kids hakkında bilgi almak istiyorum.';
  
  // WhatsApp link oluştur
  const whatsappLink = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(defaultMessage)}`;
  
  const handleClick = () => {
    // Eğer az önce sürükleme yapıldıysa tıklama yapma
    if (isDragging || dragStartPos.current.wasDragging) {
      dragStartPos.current.wasDragging = false;
      return;
    }
    
    // Analytics tracking (opsiyonel)
    if (window.gtag) {
      window.gtag('event', 'whatsapp_click', {
        event_category: 'engagement',
        event_label: 'WhatsApp Widget'
      });
    }
    
    // WhatsApp'ı yeni sekmede aç
    window.open(whatsappLink, '_blank', 'noopener,noreferrer');
  };
  
  // Minimize/Maximize toggle
  const handleToggle = (e) => {
    e.stopPropagation();
    
    // Eğer sürükleme yapıldıysa toggle yapma
    if (dragStartPos.current.wasDragging) {
      dragStartPos.current.wasDragging = false;
      return;
    }
    
    setIsMinimized(!isMinimized);
  };
  
  // Desktop: Mouse ile sürükleme
  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // Sadece sol tık
    
    // Eğer minimize durumdaysa, uzun basınca sürükle
    // Normal durumdaysa, hemen sürüklenebilir yap
    const delay = isMinimized ? 500 : 500;
    
    longPressTimer.current = setTimeout(() => {
      setIsDragging(true);
      dragStartPos.current = { x: e.clientX, y: e.clientY };
    }, delay);
  };
  
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const deltaX = dragStartPos.current.x - e.clientX;
    const deltaY = dragStartPos.current.y - e.clientY; // DÜZELTİLDI: Y yönü tersine çevrildi
    
    setPosition(prev => ({
      bottom: Math.max(80, Math.min(window.innerHeight - 100, prev.bottom + deltaY)),
      right: Math.max(10, Math.min(window.innerWidth - 100, prev.right + deltaX))
    }));
    
    dragStartPos.current = { x: e.clientX, y: e.clientY };
  };
  
  const handleMouseUp = () => {
    clearTimeout(longPressTimer.current);
    if (isDragging) {
      savePosition(position);
      dragStartPos.current.wasDragging = true; // Sürükleme bitti flag'i
      setIsDragging(false);
      
      // Kısa bir süre sonra flag'i temizle
      setTimeout(() => {
        dragStartPos.current.wasDragging = false;
      }, 100);
    }
  };
  
  // Mobil: Touch ile sürükleme
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    dragStartPos.current.touchStartX = touch.clientX;
    dragStartPos.current.touchStartY = touch.clientY;
    dragStartPos.current.touchMoved = false;
    
    longPressTimer.current = setTimeout(() => {
      // Sadece parmak hareket etmediyse drag modu aktif
      if (!dragStartPos.current.touchMoved) {
        setIsDragging(true);
        dragStartPos.current = { 
          x: touch.clientX, 
          y: touch.clientY,
          touchMoved: false
        };
      }
    }, 500);
  };
  
  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    
    // Hareket tespit et
    if (dragStartPos.current.touchStartX && dragStartPos.current.touchStartY) {
      const deltaX = Math.abs(touch.clientX - dragStartPos.current.touchStartX);
      const deltaY = Math.abs(touch.clientY - dragStartPos.current.touchStartY);
      
      // 10px'den fazla hareket varsa, bu bir drag
      if (deltaX > 10 || deltaY > 10) {
        dragStartPos.current.touchMoved = true;
        clearTimeout(longPressTimer.current); // Hareket varsa long press iptal
      }
    }
    
    if (!isDragging) return;
    e.preventDefault();
    
    const deltaX = dragStartPos.current.x - touch.clientX;
    const deltaY = dragStartPos.current.y - touch.clientY;
    
    // Mobilde daha smooth hareket için requestAnimationFrame kullan
    requestAnimationFrame(() => {
      setPosition(prev => ({
        bottom: Math.max(80, Math.min(window.innerHeight - 100, prev.bottom + deltaY)),
        right: Math.max(10, Math.min(window.innerWidth - 100, prev.right + deltaX))
      }));
    });
    
    dragStartPos.current = { 
      ...dragStartPos.current,
      x: touch.clientX, 
      y: touch.clientY 
    };
  };
  
  const handleTouchEnd = () => {
    clearTimeout(longPressTimer.current);
    
    if (isDragging) {
      savePosition(position);
      dragStartPos.current.wasDragging = true; // Sürükleme bitti flag'i
      setIsDragging(false);
      
      // Kısa bir süre sonra flag'i temizle
      setTimeout(() => {
        dragStartPos.current.wasDragging = false;
      }, 100);
    } else {
      // Drag yapılmadıysa ve hareket de yoksa, bu bir tıklama
      if (!dragStartPos.current.touchMoved) {
        dragStartPos.current.wasDragging = false;
      }
    }
    
    // Touch state'i temizle
    dragStartPos.current.touchMoved = false;
    dragStartPos.current.touchStartX = null;
    dragStartPos.current.touchStartY = null;
  };
  
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, position]);
  
  // Minimize edildiğinde sürükleme handler'larını temizle
  useEffect(() => {
    if (isMinimized) {
      setIsDragging(false);
      clearTimeout(longPressTimer.current);
    }
  }, [isMinimized]);

  return (
    <div 
      ref={widgetRef}
      className={`whatsapp-widget ${isDragging ? 'dragging' : ''} ${isMinimized ? 'minimized' : ''}`}
      style={{
        bottom: `${position.bottom}px`,
        right: `${position.right}px`
      }}
    >
      {isMinimized ? (
        /* Minimize edildiğinde sadece açma butonu göster */
        <button
          className="whatsapp-show-button"
          onClick={handleToggle}
          onMouseDown={!isMobile ? handleMouseDown : undefined}
          onTouchStart={isMobile ? undefined : handleTouchStart}
          aria-label="WhatsApp widget'ı göster"
          title={isMobile ? "WhatsApp'ı göster" : "WhatsApp'ı göster (Uzun basarak taşıyın)"}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
          </svg>
        </button>
      ) : (
        <>
      
      <button
        className={`whatsapp-button ${isHovered ? 'hovered' : ''} ${isDragging ? 'dragging' : ''}`}
        onClick={handleClick}
        onMouseDown={!isMobile ? handleMouseDown : undefined}
        onTouchStart={isMobile ? undefined : handleTouchStart}
        onMouseEnter={() => !isDragging && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-label="WhatsApp ile iletişime geç"
        title={isMobile ? 'WhatsApp ile bize ulaşın' : (isDragging ? 'Sürükl eniyor...' : 'WhatsApp ile bize ulaşın (Uzun basarak taşıyın)')}
      >
        {/* WhatsApp Icon SVG */}
        <svg
          className="whatsapp-icon"
          viewBox="0 0 32 32"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="currentColor"
            d="M16 0C7.163 0 0 7.163 0 16c0 2.828.736 5.482 2.023 7.777L.057 30.863a.5.5 0 0 0 .652.652l7.086-1.966A15.947 15.947 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.455A13.41 13.41 0 0 1 9.146 27.4l-.373-.223-4.444 1.232 1.232-4.444-.223-.373A13.41 13.41 0 0 1 2.545 16 13.455 13.455 0 1 1 16 29.455z"
          />
          <path
            fill="currentColor"
            d="M23.363 19.636c-.397-.199-2.348-1.159-2.712-1.291-.363-.132-.627-.199-.891.199-.264.397-1.025 1.291-1.256 1.556-.231.264-.463.298-.86.099-.397-.199-1.677-.618-3.194-1.972-1.181-1.053-1.978-2.353-2.209-2.75-.231-.397-.025-.612.174-.81.179-.178.397-.463.595-.694.198-.231.264-.397.397-.661.132-.264.066-.496-.033-.694-.099-.199-.891-2.147-1.221-2.94-.322-.773-.649-.669-.891-.681-.231-.012-.496-.015-.76-.015s-.694.099-1.058.496c-.363.397-1.388 1.356-1.388 3.307s1.421 3.836 1.62 4.1c.199.264 2.8 4.276 6.782 5.996.948.41 1.688.654 2.265.837.952.302 1.818.26 2.502.157.763-.114 2.348-.96 2.679-1.887.331-.927.331-1.721.231-1.887-.099-.165-.363-.264-.76-.463z"
          />
        </svg>
        
        {/* Tooltip (hover'da görünen metin) */}
        {isHovered && !isDragging && (
          <span className="whatsapp-tooltip">
            {isDragging ? 'Sürükle' : 'Bize WhatsApp\'tan yazın'}
          </span>
        )}
          {/* Minimize/Close butonu */}
          {!isDragging && (
            <button
              className="whatsapp-close"
              onClick={handleToggle}
              aria-label="WhatsApp widget'ı gizle"
              title="Gizle"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M19 13H5v-2h14v2z"/>
              </svg>
            </button>
          )}
        </button>
        
        {/* Pulse animation için outer circle (sürükleme sırasında gizli) */}
        {!isDragging && <div className="whatsapp-pulse"></div>}
        
        {/* Sürükleme hint'i */}
        {isDragging && (
          <div className="drag-hint">
            Bırakmak için serbest bırakın
          </div>
        )}
      </>
      )}
    </div>
  );
};

export default WhatsAppWidget;
